// ═══════════════════════════════════════════════════════════
// API Client — Typed fetch wrapper (TPP §2.3)
// ═══════════════════════════════════════════════════════════

export interface ApiResponse<T> {
  data: T;
  total?: number;
  page?: number;
  pageSize?: number;
}

export interface ApiError {
  error: { code: string; message: string; details?: unknown } | string;
  code?: string;
  details?: unknown;
}

/** Request params for paginated list endpoints */
export interface ListRequest {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  filters?: Record<string, unknown>;
  dateFrom?: string;
  dateTo?: string;
}

function getBaseUrl(): string {
  // In development, Vite proxy handles /api → worker
  // In production (B24 iframe), we use the same origin
  return "";
}

// We import the store at the top level — Zustand stores are safe for module-level import
import { useAuthStore } from "@/stores/auth.store";
import i18n from "@i18n";

function getMemberId(): string {
  return useAuthStore.getState().memberId ?? "";
}

function getIsAdmin(): boolean {
  return useAuthStore.getState().isAdmin ?? false;
}

function getUserId(): string {
  const state = useAuthStore.getState();
  // Prefer currentUser.id — sourced from the live user.current B24 API call
  // in BitrixProvider, which is more reliable than authData.user_id from the
  // OAuth token (which can be 0 or undefined in some iframe contexts).
  return state.currentUser?.id ?? state.userId ?? "";
}

function getUserName(): string {
  return useAuthStore.getState().currentUser?.fullName ?? "";
}

/**
 * Get the live B24 access token from the SDK frame (if available).
 * This is the REAL OAuth token issued by B24 for the current session.
 */
function getB24AccessToken(): string | null {
  const b24Frame = useAuthStore.getState().b24Frame;
  if (!b24Frame) return null;
  try {
    const authData = b24Frame.auth.getAuthData();
    return authData && typeof authData === "object" ? authData.access_token : null;
  } catch {
    return null;
  }
}

let requestCounter = 0;

function generateRequestId(): string {
  requestCounter = (requestCounter + 1) % Number.MAX_SAFE_INTEGER;
  return `fe-${Date.now()}-${requestCounter}`;
}

/** Typed fetch wrapper with auto-headers and error handling */
async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${getBaseUrl()}${path}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Member-Id": getMemberId(),
    "X-Request-Id": generateRequestId(),
    "X-User-Id": getUserId(),
    "X-User-Name": getUserName(),
    "X-User-Language": i18n.language,
    ...(getIsAdmin() ? { "X-Is-Admin": "true" } : {}),
    ...(options.headers as Record<string, string> | undefined),
  };

  // Forward the real B24 access token so the worker can call B24 REST
  // directly without needing stored OAuth tokens (essential for dev mode,
  // and reduces D1 decrypt/refresh overhead in production).
  const b24Token = getB24AccessToken();
  if (b24Token) {
    headers["X-B24-Access-Token"] = b24Token;
  }

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as ApiError;
    const errPayload = body.error;
    const message =
      typeof errPayload === "object" && errPayload !== null
        ? errPayload.message
        : typeof errPayload === "string"
          ? errPayload
          : `HTTP ${response.status}`;
    const code =
      typeof errPayload === "object" && errPayload !== null
        ? errPayload.code
        : (body.code ?? String(response.status));
    const details =
      typeof errPayload === "object" && errPayload !== null ? errPayload.details : body.details;
    const error = new Error(message);
    (error as unknown as Record<string, unknown>).status = response.status;
    (error as unknown as Record<string, unknown>).code = code;
    (error as unknown as Record<string, unknown>).details = details;
    throw error;
  }

  return response.json() as Promise<T>;
}

/** Unwrap standard { success, data } envelope */
async function requestData<T>(path: string, options: RequestInit = {}): Promise<T> {
  const envelope = await request<{ success: boolean; data: T }>(path, options);
  return envelope.data;
}

// ═══════════════════════════════════════════════════════════
// Typed API methods
// ═══════════════════════════════════════════════════════════

export const api = {
  /** Raw request — returns full JSON response */
  raw: <T>(path: string, options?: RequestInit) => request<T>(path, options),

  get: <T>(path: string) => requestData<T>(path),

  post: <T>(path: string, body: unknown) =>
    requestData<T>(path, { method: "POST", body: JSON.stringify(body) }),

  put: <T>(path: string, body: unknown) =>
    requestData<T>(path, { method: "PUT", body: JSON.stringify(body) }),

  delete: <T>(path: string) => requestData<T>(path, { method: "DELETE" }),

  /** Build query string from ListRequest params */
  listParams: (params: ListRequest): string => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.pageSize) qs.set("pageSize", String(params.pageSize));
    if (params.search) qs.set("search", params.search);
    if (params.sortBy) qs.set("sortBy", params.sortBy);
    if (params.sortDir) qs.set("sortDir", params.sortDir);
    if (params.dateFrom) qs.set("dateFrom", params.dateFrom);
    if (params.dateTo) qs.set("dateTo", params.dateTo);
    // Serialize filters into query string
    if (params.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (value !== undefined && value !== null && value !== "") {
          qs.set(`filter[${key}]`, String(value));
        }
      }
    }
    const str = qs.toString();
    return str ? `?${str}` : "";
  },
};
